import { useState, useEffect } from 'react';
import { X, Play, Info, Save } from 'lucide-react';
import { generateClient } from 'aws-amplify/api';
import { START_SCAN, UPDATE_SITE } from '../../graphql/mutations';
import { GET_SITE } from '../../graphql/queries';
import { ScanType, RateLimitPreset } from '../../types';
import { ScanTool } from './ToolSelector';
import { BASE_SCAN_TYPES, TOOL_BADGE, deriveSelectedTools } from './scanTypes';

const client = generateClient();

interface ScanConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  siteUrl: string;
  onSuccess: () => void;
  /**
   * Retained so callers need not change. Nothing here is gated on Search Console any
   * more: the outbound toxic-link check does not use it.
   */
  gscConnected?: boolean;
}

const RATE_LIMITS: { value: RateLimitPreset; label: string; description: string }[] = [
  { value: 'gentle', label: 'Gentle (Recommended)', description: '1 request every 2 seconds, max 2 concurrent' },
  { value: 'moderate', label: 'Moderate', description: '1 request per second, max 5 concurrent' },
  { value: 'aggressive', label: 'Aggressive', description: '5 requests per second, max 10 concurrent' },
];


export function ScanConfigModal({ isOpen, onClose, siteId, siteUrl, onSuccess }: ScanConfigModalProps) {
  const SCAN_TYPES = BASE_SCAN_TYPES;
  const [scanTypes, setScanTypes] = useState<ScanType[]>(['accessibility', 'eaa', 'seo', 'geo', 'performance', 'brokenLinks']);
  const [accessibilityMode, setAccessibilityMode] = useState<'quick' | 'full'>('full');
  const [wcagVersion, setWcagVersion] = useState<'2.1' | '2.2'>('2.2');
  const [wcagLevel, setWcagLevel] = useState<'AA' | 'AAA'>('AA');
  const [pageLimit, setPageLimit] = useState(100);
  const [scanAllPages, setScanAllPages] = useState(false);
  const [rateLimit, setRateLimit] = useState<RateLimitPreset>('gentle');
  const [pauseAfterCrawl, setPauseAfterCrawl] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load site defaults when modal opens
  useEffect(() => {
    if (isOpen) {
      loadSiteDefaults();
    }
  }, [isOpen, siteId]);

  const loadSiteDefaults = async () => {
    setIsLoadingDefaults(true);
    try {
      const response = await client.graphql({
        query: GET_SITE,
        variables: { siteId },
      });

      const site = (response as { data: { getSite: { pauseAfterCrawl?: boolean } } }).data.getSite;

      if (site) {
        // Load pauseAfterCrawl default
        if (typeof site.pauseAfterCrawl === 'boolean') {
          setPauseAfterCrawl(site.pauseAfterCrawl);
        }
      }
    } catch (err) {
      console.error('Error loading site defaults:', err);
      // Don't show error to user - just use default values
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  if (!isOpen) return null;

  const handleToggleScanType = (type: ScanType) => {
    setScanTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Map frontend enum values to GraphQL schema enum values
  const mapScanType = (type: ScanType): string => {
    const mapping: Record<ScanType, string> = {
      accessibility: 'ACCESSIBILITY',
      eaa: 'EAA',
      seo: 'SEO',
      geo: 'GEO',
      performance: 'PERFORMANCE',
      brokenLinks: 'BROKEN_LINKS',
      toxicLinks: 'TOXIC_LINKS',
    };
    return mapping[type];
  };

  const mapWcagVersion = (version: '2.1' | '2.2'): string => {
    return version === '2.1' ? 'WCAG_21' : 'WCAG_22';
  };

  const mapRateLimit = (rate: RateLimitPreset): string => {
    return rate.toUpperCase();
  };

  const mapScanTool = (tool: ScanTool): string => {
    return tool.toUpperCase();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (scanTypes.length === 0) {
      setError('Please select at least one scan type');
      return;
    }

    setIsSubmitting(true);

    try {
      // Save as default if checkbox is checked
      if (saveAsDefault) {
        await client.graphql({
          query: UPDATE_SITE,
          variables: {
            input: {
              siteId,
              pauseAfterCrawl,
            },
          },
        });
      }

      // Start the scan
      await client.graphql({
        query: START_SCAN,
        variables: {
          input: {
            siteId,
            config: {
              scanTypes: scanTypes.map(mapScanType),
              wcagVersion: mapWcagVersion(wcagVersion),
              wcagLevel: wcagLevel,
              pageLimit: scanAllPages ? 10000 : pageLimit,
              rateLimit: mapRateLimit(rateLimit),
              selectedTools: deriveSelectedTools(scanTypes, accessibilityMode).map(mapScanTool),
              pauseAfterCrawl,
            },
          },
        },
      });

      onSuccess();
    } catch (err) {
      console.error('Error starting scan:', err);
      setError('Failed to start scan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-primary/80" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white rounded-card shadow-xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-light">
          <div>
            <h2 className="text-heading-3 font-semibold">Configure Scan</h2>
            <p className="text-body-sm text-gray-medium mt-1">{siteUrl}</p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-gray-medium hover:text-dark-text rounded-card transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {error && (
            <div className="p-3 bg-status-error/10 text-status-error rounded-card text-body-sm">
              {error}
            </div>
          )}

          {isLoadingDefaults && (
            <div className="flex items-center gap-2 text-gray-medium text-body-sm">
              <span className="animate-spin w-4 h-4 border-2 border-coral border-t-transparent rounded-full" />
              Loading site defaults...
            </div>
          )}

          {/* Section: Scan Types */}
          <div className="space-y-4">
            <h3 className="text-body font-semibold text-gray-dark border-b border-gray-light pb-2">
              Scan Types
            </h3>
            <p className="text-caption text-gray-medium -mt-2">
              Choose what to check. The badge on each option shows which engine it runs.
            </p>
            <div className="space-y-2">
              {SCAN_TYPES.map(type => (
                <label
                  key={type.value}
                  className="flex items-start gap-3 p-3 rounded-card border border-gray-light hover:border-coral cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={scanTypes.includes(type.value)}
                    onChange={() => handleToggleScanType(type.value)}
                    disabled={isSubmitting}
                    className="mt-0.5 h-4 w-4 text-coral rounded focus:ring-coral"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{type.label}</span>
                      {(type.value === 'accessibility' && accessibilityMode === 'quick'
                        ? type.tools.filter(t => t !== 'lighthouse')
                        : type.tools
                      ).map(t => (
                        <span
                          key={t}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-caption font-medium ${TOOL_BADGE[t].className}`}
                        >
                          {TOOL_BADGE[t].label}
                        </span>
                      ))}
                    </div>
                    <div className="text-body-sm text-gray-medium">{type.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Section: Scan Options (WCAG Configuration) */}
          {scanTypes.includes('accessibility') && (
            <div className="space-y-4">
              <h3 className="text-body font-semibold text-gray-dark border-b border-gray-light pb-2">
                Scan Options
              </h3>

              {/* Accessibility scan depth: Quick (axe only) vs Full (axe + Lighthouse) */}
              <div>
                <label className="block text-body font-medium mb-2">Accessibility scan</label>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { value: 'full', label: 'Full', desc: 'axe + Lighthouse scores' },
                    { value: 'quick', label: 'Quick', desc: 'axe only, faster' },
                  ] as const).map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-2 p-3 rounded-card border cursor-pointer transition-colors
                        ${accessibilityMode === opt.value ? 'border-coral bg-coral/5' : 'border-gray-light hover:border-coral'}`}
                    >
                      <input
                        type="radio"
                        name="accessibilityMode"
                        checked={accessibilityMode === opt.value}
                        onChange={() => setAccessibilityMode(opt.value)}
                        disabled={isSubmitting}
                        className="mt-0.5 h-4 w-4 text-coral focus:ring-coral"
                      />
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-body-sm text-gray-medium">{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-body font-medium mb-2">WCAG Version</label>
                  <select
                    value={wcagVersion}
                    onChange={e => setWcagVersion(e.target.value as '2.1' | '2.2')}
                    disabled={isSubmitting}
                    className="input"
                  >
                    <option value="2.2">WCAG 2.2 (Recommended)</option>
                    <option value="2.1">WCAG 2.1</option>
                  </select>
                </div>
                <div>
                  <label className="block text-body font-medium mb-2">Conformance Level</label>
                  <select
                    value={wcagLevel}
                    onChange={e => setWcagLevel(e.target.value as 'AA' | 'AAA')}
                    disabled={isSubmitting}
                    className="input"
                  >
                    <option value="AA">Level AA (Standard)</option>
                    <option value="AAA">Level AAA (Enhanced)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Section: Crawl Settings */}
          <div className="space-y-4">
            <h3 className="text-body font-semibold text-gray-dark border-b border-gray-light pb-2">
              Crawl Settings
            </h3>

            {/* Page Limit */}
            <div>
              <label className="block text-body font-medium mb-2">Page Limit</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={pageLimit}
                  onChange={e => setPageLimit(Math.min(1000, Math.max(1, parseInt(e.target.value) || 1)))}
                  disabled={isSubmitting || scanAllPages}
                  className="input w-32"
                />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scanAllPages}
                    onChange={e => setScanAllPages(e.target.checked)}
                    disabled={isSubmitting}
                    className="h-4 w-4 text-coral rounded focus:ring-coral"
                  />
                  <span className="text-body">Scan all pages</span>
                </label>
              </div>
              <p className="text-caption text-gray-medium mt-1">
                {scanAllPages ? 'All discovered pages will be scanned' : 'Maximum pages to scan (1-1000)'}
              </p>
            </div>

            {/* Pause After Crawl */}
            <div>
              <label className="flex items-start gap-3 p-3 rounded-card border border-gray-light hover:border-coral cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={pauseAfterCrawl}
                  onChange={e => setPauseAfterCrawl(e.target.checked)}
                  disabled={isSubmitting}
                  className="mt-0.5 h-4 w-4 text-coral rounded focus:ring-coral"
                />
                <div>
                  <div className="font-medium">Pause after crawl to review discovered pages</div>
                  <div className="text-body-sm text-gray-medium">
                    The scan will pause after discovering pages, allowing you to review the list and adjust the page limit before scanning begins.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Section: Rate Limiting */}
          <div className="space-y-4">
            <h3 className="text-body font-semibold text-gray-dark border-b border-gray-light pb-2">
              Rate Limiting
            </h3>
            <div className="space-y-2">
              {RATE_LIMITS.map(rate => (
                <label
                  key={rate.value}
                  className={`flex items-start gap-3 p-3 rounded-card border cursor-pointer transition-colors
                    ${rateLimit === rate.value ? 'border-coral bg-coral/5' : 'border-gray-light hover:border-coral'}`}
                >
                  <input
                    type="radio"
                    name="rateLimit"
                    checked={rateLimit === rate.value}
                    onChange={() => setRateLimit(rate.value)}
                    disabled={isSubmitting}
                    className="mt-0.5 h-4 w-4 text-coral focus:ring-coral"
                  />
                  <div>
                    <div className="font-medium">{rate.label}</div>
                    <div className="text-body-sm text-gray-medium">{rate.description}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex items-start gap-2 mt-3 p-3 bg-status-warning/10 rounded-card">
              <Info size={16} className="text-status-warning flex-shrink-0 mt-0.5" />
              <p className="text-body-sm text-gray-dark">
                Higher rates may impact the target website's performance. Use aggressive rates only for sites you control.
              </p>
            </div>
          </div>

          {/* Save as Default */}
          <div className="pt-2">
            <label className="flex items-center gap-3 p-3 rounded-card border border-gray-light hover:border-coral cursor-pointer transition-colors bg-gray-50">
              <input
                type="checkbox"
                checked={saveAsDefault}
                onChange={e => setSaveAsDefault(e.target.checked)}
                disabled={isSubmitting}
                className="h-4 w-4 text-coral rounded focus:ring-coral"
              />
              <div className="flex items-center gap-2">
                <Save size={16} className="text-gray-medium" />
                <span className="font-medium">Save as default for this site</span>
              </div>
            </label>
            <p className="text-caption text-gray-medium mt-1 ml-10">
              Saves the pause after crawl setting as the default for future scans on this site.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-light">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || scanTypes.length === 0}
              className="btn-primary flex-1"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Starting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Play size={18} />
                  Start Scan
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
